/* eslint-disable indent */
"use client";

import { FC, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { captureException } from "@sentry/react";
import { Play, Volume2 } from "lucide-react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { Skeleton } from "@/components/ui/skeleton";
import SocialShare from "components/atoms/SocialShare";
import { DeleteConfirmationModal } from "components/molecules/DeleteModal";
import EligibleCompetitionsModal from "components/molecules/EligibleCompetitionsModal";
import MetadataSection from "components/molecules/MetadataSection";
import ShareModal from "components/molecules/ShareModal";
import StatsSection from "components/molecules/StatsSection";
import { deleteImage, getCommentsByImage, getMonsterStatsByImageId, getNPCStatsByImageId, onToggleVisibility, voteOnImage } from "lib/actions";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, GameEntityDTO, ICommentDTO, ImageDTO, RequestType, UserPremiumType } from "types";
import * as Styled from "./ImagePage.styled";
const CommentSection = dynamic(() => import("components/molecules/CommentSection"), {
  ssr: false
});
const ControlsContainer = dynamic(() => import("components/molecules/ControlsContainer"), {
  ssr: false
});
const ReactPlayer = dynamic(() => import("react-player"), {
  ssr: false
});
const ReactAudioPlayer = dynamic(() => import("react-audio-player"), {
  ssr: false
});
interface ImagePageProps {
  imageDto: ImageDTO;
}
const placeholderDataUrl = "data:image/svg+xml;base64,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";
export const ImagePage: FC<ImagePageProps> = ({
  imageDto
}) => {
  const router = useRouter();
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [originalImage, setOriginalImage] = useState<ImageDTO>(imageDto);
  const [selectedImage, setSelectedImage] = useState<ImageDTO>(imageDto); // This will change based on user selection
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showEligibleCompetitionsModal, setShowEligibleCompetitionsModal] = useState(false);
  const [comments, setComments] = useState<ICommentDTO[]>([]);
  const [stats, setStats] = useState<GameEntityDTO | null>(null);
  const [imageDimensions, setImageDimensions] = useState({
    width: 1,
    height: 1
  });
  const [loadingComments, setLoadingComments] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const handleInpaintClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      router.push({
        pathname: "/edit",
        query: {
          editMode: "Spot Heal",
          imageId: imageId
        }
      });
    }
  };
  useEffect(() => {
    setOriginalImage(imageDto);
    setSelectedImage(imageDto);
  }, [imageDto]);
  const handleBGRemovalClick = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (selectedImage) {
      router.push({
        pathname: "/edit",
        query: {
          editMode: "Remove Background",
          imageId: selectedImage.id
        }
      });
    }
  };
  const handleUpscalerModalClick = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (selectedImage.imageMetadata.npcFieldsMap["Edit Type"] === "UPSCALER") {
      toast.error("This image has already been upscaled and cannot be upscaled again.");
    } else {
      if (selectedImage) {
        router.push({
          pathname: "/edit",
          query: {
            editMode: "Upscale Image",
            imageId: selectedImage.id
          }
        });
      }
    }
  };
  const handleADetailerClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      router.push({
        pathname: "/edit",
        query: {
          imageId: imageId
        }
      });
    }
  };
  const handleFaceSwapClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      router.push({
        pathname: "/edit",
        query: {
          editMode: "Face Swap",
          imageId: imageId
        }
      });
    }
  };
  const handleEditClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      router.push({
        pathname: "/edit",
        query: {
          editMode: "Spot Heal",
          imageId: imageId
        }
      });
    }
  };
  const handleAudioClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      router.push({
        pathname: "/edit",
        query: {
          editMode: "Apply Audio",
          imageId: imageId
        }
      });
    }
  };
  const handleShareClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsShareModalOpen(true);
  };
  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
  };
  const handleCompetitionsClick = () => {
    setShowEligibleCompetitionsModal(true);
  };
  const handleCloseEligibleCompetitionsModal = () => {
    setShowEligibleCompetitionsModal(false);
    dispatch({
      type: AppReducerActionType.TOGGLE_OVERLAY_VISIBILITY,
      payload: {
        ...state,
        isOverlayVisible: false
      }
    });
  };
  const prepareForDelete = () => {
    setShowDeleteConfirmation(true);
  };
  const handleConfirmDelete = async () => {
    try {
      await deleteImage(selectedImage.id);
      if (selectedImage.id === originalImage.id) {
        // If the original image is being deleted, route to the home page
        router.push("/");
      } else {
        // If a child image is being deleted, update the original image's child images
        const updatedChildImages = (originalImage.childImages || []).filter(child => child.id !== selectedImage.id);
        const updatedOriginalImage = {
          ...originalImage,
          childImages: updatedChildImages
        };
        setOriginalImage(updatedOriginalImage);
        setSelectedImage(updatedOriginalImage);
      }
      setShowDeleteConfirmation(false);
    } catch (error: any) {
      captureException("Failed to delete image", error);
      setShowDeleteConfirmation(false);
    }
  };
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
  };
  const handleToggle = async (imageId: string) => {
    const currentVisibility = selectedImage.public;
    const newVisibility = !currentVisibility;
    try {
      await onToggleVisibility(imageId, newVisibility);
      setSelectedImage(prev => ({
        ...prev,
        public: newVisibility
      }));
      originalImage.childImages?.forEach(child => {
        if (child.id === imageId) {
          child.public = newVisibility;
        }
      });
    } catch (error: any) {
      captureException("Failed to update visibility status.", error);
    }
  };
  useEffect(() => {
    if (selectedImage) {
      const fetchComments = async () => {
        const response = await getCommentsByImage(selectedImage.id, "top");
        if (response.comments) {
          setComments(response.comments);
          setLoadingComments(false);
        } else if (response.error) {
          console.error("Failed to fetch comments:", response.error);
        }
      };
      fetchComments();
    }
  }, [selectedImage]);
  const handleImageClick = (image: ImageDTO) => {
    setSelectedImage(image);
  };
  const handleViewClick = (imageId: string) => {
    router.push(`/images/${imageId}`);
  };
  const formatRequestType = (requestType: string) => {
    if (!requestType) return "";
    return requestType.charAt(0).toUpperCase() + requestType.slice(1).toLowerCase();
  };
  useEffect(() => {
    if (selectedImage) {
      const fetchStats = async () => {
        let response;
        try {
          switch (selectedImage.imageMetadata.requestType) {
            case RequestType.MONSTER:
              response = await getMonsterStatsByImageId(selectedImage.id);
              break;
            case RequestType.NPC:
              response = await getNPCStatsByImageId(selectedImage.id);
              break;
            default:
              setLoadingStats(false);
              return;
          }
          if (!response) {
            setStats(null);
            setLoadingStats(false);
          } else {
            setStats(response);
            setLoadingStats(false);
          }
        } catch (error: any) {
          captureException("Failed to fetch stats", error);
          setLoadingStats(false);
        }
      };
      fetchStats();
    }
  }, [selectedImage]);
  const {
    theme,
    username,
    description,
    npcFieldsMap,
    requestType
  } = selectedImage.imageMetadata;
  const race = npcFieldsMap?.["Race"] || "";
  const characterClass = npcFieldsMap?.["Class"] || "";
  const occupation = npcFieldsMap?.["Occupation"] || "";
  const monster = npcFieldsMap?.["Monster"] || "";
  const itemName = npcFieldsMap?.["Item Name"] || "";
  const landscapeName = npcFieldsMap?.["Landscape Name"] || "";
  const battleMapName = npcFieldsMap?.["Battlemap Name"] || "";
  const itemType = npcFieldsMap?.["Item Type"] || "";
  const npcDescription = [race, characterClass, occupation, monster].filter(Boolean).join(" ") || description;
  const characterDescription = npcFieldsMap?.["description"] || "";
  const baseTitle = `CharGen: D&D ${formatRequestType(requestType)} Art by ${username}`;
  const baseDescription = `Explore AI-generated D&D ${requestType?.toLowerCase() || ""} art`;
  const MAX_TITLE_LENGTH = 60;
  const titleOptions = {
    NPC: `${baseTitle} - ${npcDescription}`,
    CHARACTER: `${baseTitle} - ${characterDescription} in ${theme}`,
    MONSTER: `${baseTitle} - ${npcDescription} in ${theme}`,
    ITEM: `${baseTitle} - ${itemName} - ${itemType}`,
    LANDSCAPE: `${baseTitle} - ${landscapeName}`,
    BATTLEMAP: `${baseTitle} - ${battleMapName}`,
    EDIT: `${baseTitle} - ${npcDescription}`,
    VIDEO: `${baseTitle} - ${npcDescription}`,
    AUDIO: `${baseTitle} - ${npcDescription}`
  };
  const getTitle = (requestType: string) => {
    const title = titleOptions[requestType as keyof typeof titleOptions];
    return title.length > MAX_TITLE_LENGTH ? baseTitle : title;
  };
  const descriptionOptions = {
    NPC: `${baseDescription} featuring a ${npcDescription} created by ${username}. Generate your own D&D NPC art with CharGen.`,
    CHARACTER: `${baseDescription} created by ${username}. ${description} Generate your own D&D character art with CharGen.`,
    MONSTER: `${baseDescription} featuring a ${npcDescription} created by ${username}. Generate your own D&D monster art with CharGen.`,
    ITEM: `${baseDescription} featuring ${itemName} - ${itemType} created by ${username}. Generate your own Fantasy item art with CharGen.`,
    LANDSCAPE: `${baseDescription} featuring ${landscapeName} created by ${username}. Generate your own Fantasy landscape art with CharGen.`,
    BATTLEMAP: `${baseDescription} featuring ${battleMapName} created by ${username}. Generate your own D&D battlemaps with CharGen.`,
    EDIT: `${baseDescription} featuring a ${npcDescription} created by ${username}. Generate your own Fantasy Art with CharGen.`,
    VIDEO: `${baseDescription} video featuring a ${npcDescription} created by ${username}. Generate your own Fantasy Videos with CharGen.`,
    AUDIO: `${baseDescription} audio featuring a ${npcDescription} created by ${username}. Generate your own Fantasy Audio with CharGen.`
  };
  const metaTitle = getTitle(requestType);
  const metaDescription = descriptionOptions[requestType];
  const handleVote = async (imageId: string) => {
    if (!state.isLoggedIn) {
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    const newVoteStatus = !selectedImage.votedByUser;
    try {
      await voteOnImage(imageId, newVoteStatus);
      setSelectedImage(prev => ({
        ...prev,
        votedByUser: newVoteStatus,
        voteCount: prev.voteCount + (newVoteStatus ? 1 : -1)
      }));
      originalImage.childImages?.forEach(child => {
        if (child.id === imageId) {
          child.votedByUser = newVoteStatus;
          child.voteCount += newVoteStatus ? 1 : -1;
        }
      });
    } catch (error: any) {
      captureException("Failed to update vote status.", error);
    }
  };
  const isUserImage = (imageUsername: string) => state.username === imageUsername;
  const renderMetadata = () => <MetadataSection imageInfo={selectedImage} data-sentry-element="MetadataSection" data-sentry-component="renderMetadata" data-sentry-source-file="index.tsx" />;
  const renderSocialShare = () => <div className="mt-4 items-center justify-center flex flex-col" data-sentry-component="renderSocialShare" data-sentry-source-file="index.tsx">
            <SocialShare image={selectedImage} // Share the selected image
    description={metaDescription} data-sentry-element="SocialShare" data-sentry-source-file="index.tsx" />
        </div>;
  const renderComments = () => loadingComments ? <div className="p-2 w-full space-y-4">
                <div className="flex flex-row w-full justify-between h-10">
                    <Skeleton className="w-32 h-6" />
                    <Skeleton className="w-8 h-4" />
                </div>
                <div className="flex flex-col space-y-4 h-22">
                    <Skeleton className="w-full h-20 p-4 !border !border-[1px]" />
                    <Skeleton className="w-full h-10 !pt-2 !pb-2 !pl-4 !pr-4" />
                </div>
            </div> : <CommentSection image={selectedImage} initialComments={comments} />;
  const renderStats = () => loadingStats ? <div className="p-2 w-full">
                <div className="flex flex-row w-full space-x-2 mb-4">
                    <Skeleton className="w-1/3 h-28" />
                    <Skeleton className="w-2/3 h-28" />
                </div>
            </div> : <StatsSection image={selectedImage} statsProps={stats} />;
  useEffect(() => {
    const img = new Image();
    img.src = selectedImage.image;
    img.onload = () => {
      setImageDimensions({
        width: img.width,
        height: img.height
      });
    };
  }, [selectedImage.image]);
  const [showLabels, setShowLabels] = useState<boolean>(false);
  const handleResize = useCallback(() => {
    if (typeof window !== "undefined") {
      // Ensure window is defined
      if (window.innerWidth > 700) {
        setShowLabels(true);
      } else {
        setShowLabels(false);
      }
    }
  }, []);
  useEffect(() => {
    if (typeof window !== "undefined") {
      // Ensure window is defined
      window.addEventListener("resize", handleResize);
      // Initial check
      handleResize();
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [handleResize]);
  const isVideo = selectedImage.image.endsWith(".mp4");
  const isAudio = selectedImage.image.endsWith(".mp3");
  const aspectRatio = isVideo ? selectedImage.imageMetadata.npcFieldsMap?.["Size"] ? parseFloat(selectedImage.imageMetadata.npcFieldsMap["Size"].split(":")[0]) / parseFloat(selectedImage.imageMetadata.npcFieldsMap["Size"].split(":")[1]) : 1 : imageDimensions.width && imageDimensions.height ? imageDimensions.width / imageDimensions.height : 1;
  const renderCarousel = () => {
    const images = [...(originalImage.parentImage ? [originalImage.parentImage] : []), originalImage, ...(originalImage.childImages || [])];
    return <Styled.CarouselContainer data-sentry-element="unknown" data-sentry-component="renderCarousel" data-sentry-source-file="index.tsx">
                {images.map(img => <Styled.CarouselItem key={img.id}>
                        {isVideo ? <Play className="w-8 h-8 text-white" style={{
          border: "2px solid white"
        }} /> : isAudio ? <Volume2 className="w-14 h-14 text-gray-300" /> : <Styled.ThumbnailImage src={img.image} alt={img.imageMetadata.description || "Image"} width={100} height={100} layout="fixed" $isSelected={img.id === selectedImage.id} onClick={() => handleImageClick(img)} />}

                        <Styled.ViewImageButton onClick={() => handleViewClick(img.id)}>
                            <VisibilityIcon />
                        </Styled.ViewImageButton>
                    </Styled.CarouselItem>)}
            </Styled.CarouselContainer>;
  };
  const renderImage = () => !selectedImage ? <div style={{
    width: "100%",
    aspectRatio: "1/1"
  }}>
                <Skeleton className="w-full h-full" />
            </div> : isVideo ? <Styled.ImageWrapper $aspectRatio={aspectRatio}>
                <ReactPlayer url={selectedImage.image} style={{
      position: "absolute",
      top: 0,
      left: 0
    }} width="100%" height="100%" playing controls={true} loop muted playsinline />
            </Styled.ImageWrapper> : isAudio ? <div className="w-auto h-[100px] flex items-center justify-center">
                <ReactAudioPlayer src={selectedImage.image} autoPlay controls loop volume={0.5} />
            </div> : <Styled.StyledImage src={selectedImage.webpImage || selectedImage.image} alt="Artwork" width={imageDimensions.width} height={imageDimensions.height} loading="eager" priority layout="responsive" blurDataURL={placeholderDataUrl} placeholder="blur" style={{
    objectFit: "contain"
  }} />;
  return <Styled.Main data-sentry-element="unknown" data-sentry-component="ImagePage" data-sentry-source-file="index.tsx">
            <Styled.Title data-sentry-element="unknown" data-sentry-source-file="index.tsx">{metaTitle}</Styled.Title>
            <Styled.StyledModal data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Styled.ImageSection data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    {renderImage()}
                    {selectedImage && <ControlsContainer imageInfo={selectedImage} handleToggle={handleToggle} handleShareClick={handleShareClick} handleCompetitionsClick={handleCompetitionsClick} handleEditClick={handleEditClick} handleADetailerClick={handleADetailerClick} handleFaceSwapClick={handleFaceSwapClick} handleBGRemovalClick={handleBGRemovalClick} handleInpaintClick={handleInpaintClick} handleUpscaleClick={handleUpscalerModalClick} handleAudioClick={handleAudioClick} prepareForDelete={prepareForDelete} handleVote={handleVote} isUserImage={isUserImage} showLabel={showLabels} />}
                    {renderCarousel()}
                    {renderSocialShare()}
                </Styled.ImageSection>
                <Styled.MetadataSection data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    {renderMetadata()}
                    {renderStats()}
                    {renderComments()}
                </Styled.MetadataSection>
            </Styled.StyledModal>
            <ShareModal isOpen={isShareModalOpen} image={selectedImage} onClose={handleCloseShareModal} data-sentry-element="ShareModal" data-sentry-source-file="index.tsx" />
            <EligibleCompetitionsModal isOpen={showEligibleCompetitionsModal} onCancel={handleCloseEligibleCompetitionsModal} imageId={selectedImage.id} data-sentry-element="EligibleCompetitionsModal" data-sentry-source-file="index.tsx" />
            <DeleteConfirmationModal isOpen={showDeleteConfirmation} onConfirm={handleConfirmDelete} onCancel={handleCancelDelete} text="image" data-sentry-element="DeleteConfirmationModal" data-sentry-source-file="index.tsx" />
        </Styled.Main>;
};